import { useState } from "react";
import { Button } from "@omnabase/ui";
import type { Project, SendingDomain } from "@omnabase/shared";
import { SendingAddressForm, type SendingAddress } from "./SendingAddressForm";

/** The help tickets and sending domains round (2026-10-28; DECISIONS 2026-10-28): a project without a sending domain of its own chooses one
 *  of the workspace's verified domains ("Send from <domain>", keeping the project's name before the @) or adds another; it never borrows
 *  another project's records. A domain another project is still setting up is never offered, and its records show only on that project. */

/** what step 2 shows a project: its chosen domain, else a domain it is setting up (its own, the one just added, or one no project claims, a
 *  domain from before 2026-10-22), and the verified domains it may choose */
export type DomainView = { chosen: SendingDomain | null; setup: SendingDomain | null; choices: SendingDomain[] };
export const isWaiting = (r: SendingDomain) => r.status === "pending" || r.status === "failed";

export function domainViewOf(rows: SendingDomain[], p: Pick<Project, "id" | "sendingDomainId">, addedId: string | null): DomainView {
  const chosen = p.sendingDomainId ? rows.find((r) => r.id === p.sendingDomainId) ?? null : null;
  if (chosen) return { chosen, setup: null, choices: [] };
  const setup = rows.find((r) => r.id === addedId) ?? rows.find((r) => isWaiting(r) && r.projectId === p.id) ?? rows.find((r) => isWaiting(r) && !r.projectId) ?? null;
  return { chosen: null, setup, choices: rows.filter((r) => r.status === "verified" && r.id !== setup?.id) };
}

/** what "Continue" does with a typed domain: a new one is added; the one on screen keeps it and renames the part before the @; a verified
 *  domain of the workspace counts as choosing it (one Amazon identity per domain); one another project is setting up is refused, named */
export type AddRule = { kind: "new" } | { kind: "rename" } | { kind: "choose"; row: SendingDomain } | { kind: "elsewhere"; row: SendingDomain };
export function addRuleOf(rows: SendingDomain[], view: DomainView, domain: string, projectId: string): AddRule {
  const row = rows.find((r) => r.domain === domain);
  if (!row) return { kind: "new" };
  if (row.id === (view.chosen ?? view.setup)?.id) return { kind: "rename" };
  if (row.status === "verified") return { kind: "choose", row };
  if (isWaiting(row) && row.projectId && row.projectId !== projectId) return { kind: "elsewhere", row };
  return { kind: "new" }; // the server answers why it cannot be added
}

/** "Delete domain" belongs to the project that added the domain (any project when none is recorded): a borrowing project leaves through
 *  "An address on our domain", so it never deletes the domain another project sends from */
export const deletesDomain = (row: SendingDomain, projectId: string) => !row.projectId || row.projectId === projectId;

/** the verified domains as plain choices, each with the address it would send from */
export function DomainChoices({ local, choices, onChoose }: { local: string; choices: SendingDomain[]; onChoose: (row: SendingDomain) => void }) {
  if (!choices.length) return null;
  return (
    <div className="ob-domain-choices">
      <p className="ob-dns__line">Your workspace already sends from {choices.length === 1 ? "this domain" : "these domains"}. Choose one, or add another.</p>
      <ul className="ob-domain-choices__list">
        {choices.map((r) => (
          <li key={r.id} className="ob-domain-choices__row">
            <span className="ob-domain-choices__text"><strong className="ob-domain-choices__domain">{r.domain}</strong><span className="ob-domain-choices__address">{local}@{r.domain}</span></span>
            <Button variant="primary" size="sm" onClick={() => onChoose(r)}>Send from {r.domain}</Button>
          </li>
        ))}
      </ul>
    </div>
  );
}

/** "Add another domain" opens the address form; with no domain to choose the form shows alone */
export function AddAnotherDomain({ alone, supportAddress, onContinue }: { alone: boolean; supportAddress: string | null; onContinue: (a: SendingAddress) => Promise<boolean> }) {
  const [open, setOpen] = useState(false);
  if (alone || open) return <SendingAddressForm supportAddress={supportAddress} onContinue={onContinue} />;
  return <div className="ob-domain-choices__add"><Button size="sm" onClick={() => setOpen(true)}>Add another domain</Button></div>;
}
